import React, { useEffect, useState } from "react";
import { message, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import {
  updateVisitStageStatus,
  updateVisitStageStatusReferralAcceptance,
} from "../../../apis/patientService";
import { fetchStepperData } from "../../../models/preAuthSlice";
import {
  getBasicDetails,
  getTimelineDetails,
} from "../../../models/patientSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { current } from "@reduxjs/toolkit";

interface StatusOption {
  id: number;
  state: string;
}

interface ManualStatusProps {
  stateData: StatusOption[] | null;
  status: number | null;
  visitId: number;
  visitStageId: number;
  isUpdateable: boolean;
  isCompleted?: any;
}

const ManualStatus: React.FC<ManualStatusProps> = ({
  stateData,
  status,
  visitId,
  visitStageId,
  isUpdateable,
  isCompleted,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [selectedStatusValue, setSelectedStatusValue] =
    useState<StatusOption | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { patients, preAuth, auth } = useSelector((state: RootState) => state);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([
    { id: 8, state: "Draft" },
    { id: 9, state: "Pending Review" },
    { id: 10, state: "Pending More Info" },
    { id: 2, state: "Initiated" },
    { id: 3, state: "Denied" },
    { id: 4, state: "Submitted" },
    { id: 11, state: "Query" },
    { id: 7, state: "Error" },
  ]);

  const fetchStepperForStatus = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      await dispatch(fetchStepperData({ caseId: caseId, visitId: visitId }));
    }
  };

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  };

  const submittedState = stateData?.find((item) => item?.id == 4);

  useEffect(() => {
    if (stateData) {
      setStatusOptions(stateData);
    }
    if (status) {
      const filterStatus = stateData?.find((item) => item?.id == status);
      filterStatus?.state && setCurrentStatus(filterStatus?.state);
    }
  }, [stateData, status]);

  const handleEditClick = () => {
    setIsEditing(true);
    const selectedOption = statusOptions.find((option) =>
      option.state
        .toLocaleUpperCase()
        .includes(currentStatus?.toLocaleUpperCase() || ""),
    );
    setSelectedStatusValue(selectedOption ?? null);
  };

  const save = async (value: StatusOption) => {
    try {
      const isReferralAcceptanceTab =
        preAuth?.stepperData?.visitStages?.find(
          (stage: any) => stage?.visitStageId === visitStageId,
        )?.visitStage === "ClaimId Generation";

      if (isReferralAcceptanceTab) {
        const res = await updateVisitStageStatusReferralAcceptance(
          visitId,
          visitStageId,
          value.id,
          {
            referralAcceptanceOnly: true,
            fromFo: auth?.role?.role === "FO_STAFF" ? true : false,
          },
        );
        if (res) {
          message.success("Status Updated Successfully");
          getTimelineDetail();
          fetchStepperForStatus();

          dispatch(
            getBasicDetails({
              id: visitId,
            }),
          );
        }
      } else {
        const res = await updateVisitStageStatus(
          visitId,
          visitStageId,
          value.id,
        );
        if (res) {
          message.success("Status Updated Successfully");
          getTimelineDetail();
          fetchStepperForStatus();

          dispatch(
            getBasicDetails({
              id: visitId,
            }),
          );
        }
      }
    } catch (err: any) {
      message.error(err?.message || "Please, Try again later");
      fetchStepperForStatus();
      throw new Error(err?.message || "Error updating status");
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setSelectedStatusValue(null);
  };

  const handleSaveClick = async () => {
    if (selectedStatusValue) {
      try {
        await save(selectedStatusValue);
        setCurrentStatus(selectedStatusValue.state);
      } catch (err) {}
    }
    setIsEditing(false);
  };

  const handleChange = (value: string) => {
    const selectedOption = statusOptions.find(
      (option) => option.id === Number(value),
    );
    setSelectedStatusValue(selectedOption || null);
  };

  return (
    <>
      <div
        className="status-box"
        style={{ background: "#F2F6F9", color: "black" }}
      >
        <div className="status-content">
          <span
            style={{ fontWeight: 400, color: "#575757", fontSize: "14px" }}
            className="status-label"
          >
            Status:
          </span>
          <div>
            {isEditing ? (
              <select
                className="status-value"
                value={selectedStatusValue?.id || ""}
                onChange={(e) => handleChange(e.target.value)}
              >
                <option
                  value=""
                  disabled
                  style={{ textTransform: "uppercase", fontSize: "14px" }}
                >
                  -- Select a Status --
                </option>
                {statusOptions?.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                    style={{ fontSize: "14px" }}
                  >
                    {option.state}
                  </option>
                ))}
              </select>
            ) : (
              <span
                style={{ fontWeight: 600, color: "black", fontSize: "14px" }}
                className="claim-id-value"
              >
                {currentStatus || "-"}
              </span>
            )}
            {currentStatus === "Submitted" ||
            currentStatus === "Initiated" ||
            currentStatus === "Manually Submitted" ||
            currentStatus === "Query Replied" ||
            currentStatus === "Query" ||
            currentStatus === "Pending" ||
            currentStatus === "Quality Check Approved" ||
            currentStatus === "Updated" || currentStatus?.toLowerCase() === "id generated" || currentStatus?.toLowerCase()?.includes("verified") ||
            isCompleted ? (
              !isEditing && isUpdateable ? (
                // && submittedState?.state &&
                // !currentStatus?.toLocaleUpperCase()?.includes(submittedState?.state?.toLocaleUpperCase())
                <Tooltip title="Edit Status">
                  <EditOutlined
                    style={{
                      marginLeft: "8px",
                      cursor: "pointer",
                      color: "black",
                      fontSize: "14px",
                    }}
                    onClick={handleEditClick}
                  />
                </Tooltip>
              ) : (
                isUpdateable && (
                  <>
                    <Tooltip title="Cancel">
                      <CloseOutlined
                        style={{
                          marginLeft: "8px",
                          cursor: "pointer",
                          color: "black",
                          fontSize: "14px",
                        }}
                        onClick={handleCancelClick}
                      />
                    </Tooltip>
                    <Tooltip title="Save">
                      <CheckOutlined
                        style={{
                          marginLeft: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                        onClick={handleSaveClick}
                      />
                    </Tooltip>
                  </>
                )
              )
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualStatus;
